import { ParentProps, For, createContext, createEffect, onCleanup, onMount, createMemo, Show, createSignal } from 'solid-js'
import { requireContext } from 'common'
import { CollaborativeEditContext, Collaborator, EditSource, PropertySavedHandler, ValueSetHandler } from './CollaborativeEditContext'
import Quill from 'quill'
import QuillCursors from 'quill-cursors'
import { collaborativeEdit as api } from 'st-app/api'
import 'quill/dist/quill.core.css'
import 'quill/dist/quill.snow.css'
import 'quill/dist/quill.bubble.css'
import styles from './st-collaborative-edit.module.css'
import Button from '../st-button/st-button'
import ButtonGroup from '../st-button-group/st-button-group'
import { Portal } from 'solid-js/web'

Quill.register('modules/cursors', QuillCursors)

const CollaborativeEditContextJsx = createContext<CollaborativeEditContext>();
export const requireCollaborativeEditContext = () => {
    return requireContext(CollaborativeEditContextJsx);
}

export class STGeneratorEvents {
    insertTemplate?: (templateContents: string) => void;
}

export class CollaborativeEditProps {
    srcName: string = '';
    identity: string = '';
    originalValue?: string = '';
    richText?: boolean = false
    placeholder?: string = ''
    rows?: string = ''
    cols?: string = ''
    label?: string = ''
    readonly?: boolean = false
    onValueSet?: ValueSetHandler = () => {}
    onPropertySaved?: PropertySavedHandler = () => {}
    events?: STGeneratorEvents = new STGeneratorEvents();
}

export default function CollaborativeEdit(props: ParentProps<CollaborativeEditProps>) {
    const srcName = createMemo(() => props.srcName)
    const identity = createMemo(() => props.identity)
    const readonly = createMemo(() => props.readonly ?? false)
    
    const context = new CollaborativeEditContext(srcName, identity, readonly, props.richText ?? false, props.onValueSet, props.onPropertySaved);

    if (props.events) {
        props.events.insertTemplate = (templateContents: string) => {
            context.session?.editor?.insertText(context.session?.editor.getSelection()?.index ?? 0, templateContents, 'user');
        }
    }

    return (
        <CollaborativeEditContextJsx.Provider value={context}>
            <CollaborativeEditInternal {...props} />
        </CollaborativeEditContextJsx.Provider>
    )
}


function CollaborativeEditInternal(props: ParentProps<CollaborativeEditProps>) {
    const context = requireCollaborativeEditContext()
    const [cols, setCols] = createSignal(props.cols ? parseInt(props.cols) : 0)
    const [rows, setRows] = createSignal(props.rows ? parseInt(props.rows) : 0)

    const width = createMemo(() => `${cols()}ch`)
    const height = createMemo(() => `calc(${rows()}lh)`)

    const cleanup = () => {
        context.session?.destroy();
    }

    onMount(() => {
        window.addEventListener('beforeunload', cleanup)
    });

    onCleanup(() => {
        window.removeEventListener('beforeunload', cleanup)
        cleanup();
    })

    createEffect(() => {
        context.session?.editor?.enable(!props.readonly);
        if (props.readonly) {
            context.session?.destroy();
        }
    })

    const init = (el: HTMLDivElement) => {
        context.setContainer(el);
    }

    const showToolbar = createMemo(() => context.isFocused() || context.isFullscreen())

    let outerEl: HTMLDivElement | undefined = undefined

    createEffect((wasFocused: boolean | undefined) => {
        if (wasFocused && !context.isFocused()) {
            context.save();
        }

        return context.isFocused()
    })



    return (
        <div ref={outerEl}>
            <Portal mount={context.isFullscreen() ? document.body : outerEl}>
                <div class={styles['st-collaborative-edit']} style={!context.isFullscreen() ? { width: width(), height: height() } : { }} data-readonly={context.readonly()} data-focused={context.isFocused()} data-fullscreen={context.isFullscreen()} data-richtext={!!props.richText}>
                    <Show when={showToolbar()}>
                        <div class={styles['overlay']} />

                        <div class={styles['toolbar']}>
                            <div class={styles['label']}>
                                <img src='Images/ST/CollaborativeEdit.svg' width="20" height="20"></img>
                                {props.label}
                            </div>

                            <div class={styles['toolbar-actions']}>
                                <Button style='font-size: 0.7em;' icon='fas fa-file' onClick={(e: MouseEvent) => {
                                    if (window.ST.Actions.EditText && window.ST.Actions.EditText.ShowTemplates) {
                                        window.ST.Actions.EditText.ShowTemplates.call(e.currentTarget);
                                    }
                                }} />

                                <Show when={!context.isFullscreen()}>
                                    <ButtonGroup>
                                        <Button style='font-size: 0.7em;' icon='fas fa-chevron-down' onClick={() => setRows(Math.max(5, rows() - 5))} />
                                        <Button style='font-size: 0.7em;' icon='fas fa-chevron-up' onClick={() => setRows(rows() + 5)} />
                                    </ButtonGroup>
                                </Show>

                                <Button style='font-size: 0.7em;' icon='fas fa-arrows-alt' onClick={() => context.setIsFullscreen(!context.isFullscreen())} />

                                <div class='template'></div>
                            </div>

                            <div class={styles['toolbar-status']}>
                                <Show when={context.isSaving()}>
                                    <span style='color: green;'>
                                        <i class='fas fa-rotate fa-spin'></i>
                                        Saving...
                                    </span>
                                </Show>
                                <Show when={!context.isSaving()}>
                                    <i class='fas fa-cloud-arrow-up'></i>
                                    Up To Date
                                </Show>
                            </div>

                            <div class={styles['toolbar-collaborators']}>
                                <For each={context.activeCollaborators()}>{(collaborator) => (
                                    <CollaboratorAvatar collaborator={collaborator} />
                                )}</For>
                            </div>

                        </div>
                    </Show>

                    <Show when={!showToolbar()}>
                        <div class={styles['non-focused-toolbar']}>
                            <Show when={context.isSaving()}>
                                <div class={styles['toolbar-status']} style='color: green; flex-grow: 1; display: flex; align-items: center; justify-content: end; gap: 4px;'>
                                    <i class='fas fa-rotate fa-spin'></i>
                                    Saving...
                                </div>
                            </Show>
                            <For each={context.activeCollaborators()}>{(collaborator) => (
                                <CollaboratorAvatar collaborator={collaborator} />
                            )}</For>
                        </div>
                    </Show>

                    <div class={styles['editor']}  ref={init} />
                </div>
            </Portal>
        </div>
    );
}

CollaborativeEdit.defaultProps = new CollaborativeEditProps();

const CollaboratorAvatar = (props: { collaborator: Collaborator }) => {
    const [rHex, gHex, bHex] = props.collaborator.color.slice(1).match(/.{1,2}/g) ?? ['00', '00', '00']
    const R = lin(parseInt(rHex, 16))
    const G = lin(parseInt(gHex, 16))
    const B = lin(parseInt(bHex, 16))

    const L = 0.2126 * R + 0.7152 * G + 0.0722 * B

    const contrastBlack = (L + 0.05) / 0.05;
    const contrastWhite = 1.05 / (L + 0.05);

    const textColor = (contrastBlack > contrastWhite) ? '#000000' : '#FFFFFF'

    return (
        <div use:tooltip={props.collaborator.name} style={{
            'background-color': props.collaborator.color,
            'color': textColor,
            'border-radius': '50%',
            'width': '28px',
            'height': '28px',
            'display': 'flex',
            'align-items': 'center',
            'justify-content': 'center',
        }}>
            {props.collaborator.initials}
        </div>
    )
}

const lin = (c: number) => {
    c = c/255;
    return (c <= 0.03928)
      ? c/12.92
      : ((c + 0.055)/1.055) ** 2.4;
}

